import * as Api from '@ViewModels';
import FacebookLogoUrl from '../../web/assets/2021_Facebook_icon.svg';
import InstagramLogoUrl from '../../web/assets/Instagram_logo_2022.svg';
import LinkedInLogoUrl from '../../web/assets/LinkedIn_icon.svg';
import { SocialMediaPostStatusDetails } from '../index';

/**
 * Gets social media platform logo URL
 * @source Original location: UiUtils.ts.backup line 315
 */
export const getSocialMediaPlatformLogoUrl = (platform?: Api.SocialMediaType) => {
	let platformLogoUrl = '';

	switch (platform) {
		case Api.SocialMediaType.Facebook:
			platformLogoUrl = FacebookLogoUrl;
			break;
		case Api.SocialMediaType.LinkedIn:
			platformLogoUrl = LinkedInLogoUrl;
			break;
		case Api.SocialMediaType.Instagram:
			platformLogoUrl = InstagramLogoUrl;
			break;
		default:
			break;
	}

	return platformLogoUrl;
};

/**
 * Checks social citation
 * @source Original location: UiUtils.ts.backup line 2259
 */
export const checkSocialCitation = (post: Api.ISocialMediaPost | Api.SocialMediaPostViewModel | Api.ITemplate) => {
	return post?.citation != null && post.citation.length > 0;
};

/**
 * Gets status group options
 * @source Original location: UiUtils.ts.backup line 2444
 */
export const getStatusGroupOptions = (post: Api.SocialMediaPostViewModel) => {
	if (post && [Api.PostStatus.Cancelled, Api.PostStatus.Failed, Api.PostStatus.Unknown].includes(post.status)) {
		return SocialMediaPostStatusDetails.CancelledOrFailed;
	} else if (!post || post.status === Api.PostStatus.Draft) {
		return SocialMediaPostStatusDetails.NewOrDraft;
	} else {
		return SocialMediaPostStatusDetails.NotCancelledFailedDraftOrUnknown;
	}
};

/**
 * Checks if post is scheduled, pending, draft, or new
 * @source Original location: UiUtils.ts.backup line 2454
 */
export const isScheduledOrPendingOrDraftOrNew = (
	post: Api.SocialMediaPostViewModel | Api.SocialMediaPostReportViewModel
) => {
	return !post ||
		post?.status === Api.PostStatus.Scheduled ||
		post?.status === Api.PostStatus.Pending ||
		post?.status === Api.PostStatus.Draft
		? true
		: false;
};

/**
 * Maps social media types to post targets
 * @source Original location: UiUtils.ts.backup line 2465
 */
export const mapSocialMediaTypesToPostTargets = (providerTypes: Api.SocialMediaType[]) => {
	const targets: Api.IPostTarget[] = providerTypes?.map(x => {
		return { provider: x };
	});
	return targets;
};

export const getProviderTypesFromPostTargest = (targets: Api.IPostTarget[]) => {
	const types: Api.SocialMediaType[] = targets?.map(x => {
		return x.provider;
	});
	return types;
};

export const sortPlatformTargets = (platforms: Api.IPostTarget[]) => {
	const sortedPlatforms = platforms
		?.sort((u, v) =>
			u?.state?.lastConnectionState?.postTargetDisplayName === v?.state?.lastConnectionState?.postTargetDisplayName
				? 0
				: u?.state?.lastConnectionState?.postTargetDisplayName > v?.state?.lastConnectionState?.postTargetDisplayName
					? 1
					: -1
		)
		?.sort((c, d) =>
			c?.state?.lastConnectionState?.state === d?.state?.lastConnectionState?.state
				? 0
				: c?.state?.lastConnectionState?.state > d?.state?.lastConnectionState?.state
					? 1
					: -1
		)
		?.sort((a, b) => (a.provider === b.provider ? 0 : a.provider > b.provider ? 1 : -1));
	return sortedPlatforms || [];
};

// A page target must match the same page. LinkedIn shares one userId across a member's profile and
// pages, so only fall back to userId for page-less (profile) targets — matching a page on userId
// would let an orphaned profile drag down a still-live page for the same user.
export const targetMatchesConnection = (target: Api.IPostTarget, connection: Api.ISocialMediaConnection): boolean => {
	if (!target || !connection || target.provider !== connection.type) {
		return false;
	}
	if (target.pageId) {
		return target.pageId === connection.postTargetId;
	}
	return !!target.userId && target.userId === connection.userId;
};

// The account behind an orphaned connection is gone, so nothing can be posted to it and it can't be
// reconnected: the editor stands one in as an Unknown-state placeholder, and a post whose last
// attempt outlived the account carries a last connection state that never resolved a name. A
// connected account is never orphaned, however its name happens to resolve.
export const isOrphanedConnection = (connection?: Api.ISocialMediaConnection): boolean => {
	if (!connection) {
		return false;
	}
	if (connection.state === Api.SocialMediaConnectionState.Unknown) {
		return true;
	}
	return connection.userName === 'Unknown' && connection.state !== Api.SocialMediaConnectionState.Connected;
};

export interface IPostTargetIdentity {
	pageId: string | null;
	provider: Api.SocialMediaType;
	userId: string | null;
}

// The server prunes a target by the full identity it stored, so send the post's own target when we
// still hold it and only fall back to the connection once the local prune has dropped it.
export const getPostTargetIdentity = (
	connection: Api.ISocialMediaConnection,
	targets?: Api.IPostTarget[]
): IPostTargetIdentity | null => {
	if (!connection) {
		return null;
	}
	const target = targets?.find(x => targetMatchesConnection(x, connection));
	return target
		? { pageId: target.pageId ?? null, provider: target.provider, userId: target.userId ?? null }
		: { pageId: connection.postTargetId ?? null, provider: connection.type, userId: connection.userId ?? null };
};

// Drops targets whose social account no longer exists (surfaced by the reconciliation as an
// Unknown-state placeholder connection). Those render no selectable row, so a user can't deselect
// them and a reschedule would re-send them — the post then partially fails on send.
export const removeOrphanedTargets = (
	targets: Api.IPostTarget[],
	connections: Api.ISocialMediaConnection[]
): Api.IPostTarget[] => {
	if (!targets?.length) {
		return targets ?? [];
	}
	const orphanedConnections = connections?.filter(x => isOrphanedConnection(x)) ?? [];
	if (!orphanedConnections.length) {
		return targets;
	}
	return targets.filter(target => !orphanedConnections.some(connection => targetMatchesConnection(target, connection)));
};

export const getRatingIntFromText = (ratingText: string) => {
	const numberMap = {
		ONE: 1,
		TWO: 2,
		THREE: 3,
		FOUR: 4,
		FIVE: 5,
	};
	return numberMap[ratingText as keyof typeof numberMap];
};

export function getContentLength(targetChoice: Api.SocialMediaType) {
	if (targetChoice === Api.SocialMediaType.Instagram) {
		return Api.SocialMediaPreviewLimits.Instagram;
	} else if (targetChoice === Api.SocialMediaType.LinkedIn) {
		return Api.SocialMediaPreviewLimits.LinkedIn;
	}
	return Api.SocialMediaPreviewLimits.FaceBook;
}

export interface IMentionRange {
	end: number;
	facebookMention?: Api.IFacebookPageMention;
	instagramUsername?: string;
	start: number;
	text: string;
}

export interface IMentionSegment {
	facebookMention?: Api.IFacebookPageMention;
	instagramUsername?: string;
	isMention: boolean;
	text: string;
}

// A trailing period is punctuation, not part of the username.
const INSTAGRAM_CAPTION_MENTION_REGEX = /(^|[^A-Za-z0-9._])@([A-Za-z0-9._]{1,30})/g;

export const getFacebookMentionText = (mention: Api.IFacebookPageMention, content?: string) => {
	if (mention.pageName) {
		return `@${mention.pageName}`;
	}

	if (!content) {
		return '';
	}

	return content.substring(mention.contentOffset, mention.contentOffset + mention.contentLength);
};

export const getFacebookPageLocationLabel = (location?: Api.IFacebookPageLocation) => {
	const region = location?.state || location?.country;
	return [location?.city, region].filter(Boolean).join(', ');
};

export const getFacebookMentionRanges = (
	content?: string,
	mentions?: Api.IFacebookPageMention[],
	contentStartOffset = 0
): IMentionRange[] => {
	const text = content || '';
	return (mentions || [])
		.filter(mention => !!mention?.pageName && mention.contentLength > 0)
		.map(mention => ({
			end: mention.contentOffset - contentStartOffset + mention.contentLength,
			facebookMention: mention,
			start: mention.contentOffset - contentStartOffset,
			text: `@${mention.pageName}`,
		}))
		.filter(range => range.start >= 0 && range.end <= text.length && text.slice(range.start, range.end) === range.text);
};

export const getInstagramMentionRanges = (content?: string): IMentionRange[] => {
	const text = content || '';
	const ranges: IMentionRange[] = [];

	for (const match of text.matchAll(INSTAGRAM_CAPTION_MENTION_REGEX)) {
		const username = match[2].replace(/\.+$/, '');
		if (!username) {
			continue;
		}

		const start = match.index + match[1].length;
		ranges.push({
			end: start + username.length + 1,
			instagramUsername: username,
			start,
			text: `@${username}`,
		});
	}

	return ranges;
};

export const getMentionSegments = (content?: string, ranges?: IMentionRange[]): IMentionSegment[] => {
	const text = content || '';
	const claimed: IMentionRange[] = [];
	// Earlier ranges win overlaps.
	(ranges || []).forEach(range => {
		if (claimed.some(existing => range.start < existing.end && range.end > existing.start)) {
			return;
		}

		claimed.push(range);
	});

	const segments: IMentionSegment[] = [];
	let cursor = 0;
	claimed
		.sort((left, right) => left.start - right.start)
		.forEach(range => {
			if (range.start > cursor) {
				segments.push({ isMention: false, text: text.slice(cursor, range.start) });
			}

			segments.push({
				facebookMention: range.facebookMention,
				instagramUsername: range.instagramUsername,
				isMention: true,
				text: range.text,
			});
			cursor = range.end;
		});

	if (cursor < text.length || !segments.length) {
		segments.push({ isMention: false, text: text.slice(cursor) });
	}

	return segments;
};

export const stripMentionMarkers = (content?: string, ranges?: IMentionRange[]): string =>
	getMentionSegments(content, ranges)
		.map(segment => (segment.isMention ? segment.text.replace(/^@/, '') : segment.text))
		.join('');

export const INSTAGRAM_CAPTION_LIMITS = {
	hashtags: 30,
	mentions: 20,
} as const;

export const getInstagramCaptionCounts = (content?: string) => ({
	hashtags: (content || '').match(/(^|[^A-Za-z0-9_])#[A-Za-z0-9_]+/g)?.length || 0,
	mentions: getInstagramMentionRanges(content).length,
});
